import { FC } from 'react';
import { Box, Paper, Typography } from '@mui/material';
import { observer } from 'mobx-react-lite';

import { FileUploadItem } from '@components/fileUpload';
import { useTrans } from '@libs/i18n';

import { useTenantProfileUpload } from './TenantProfileUploadProvider';

/**
 * Fixed bottom-corner tray that surfaces in-flight uploads while they move from
 * the browser to S3 and get confirmed. Each row reuses the shared
 * `FileUploadItem` (progress, cancel, retry-on-fail). A row drops out on its own
 * once the confirmed document lands in the polled documents list — from there it
 * lives in the InFlight bar / hub table. Renders nothing when idle.
 */
export const UploadProgressTray: FC = observer(() => {
  const { t } = useTrans();
  const { pendingUploads, cancelUpload, retryUpload, removeFailed } =
    useTenantProfileUpload();

  if (pendingUploads.length === 0) return null;

  return (
    <Paper
      elevation={6}
      sx={{
        position: 'fixed',
        bottom: 16,
        insetInlineEnd: 16,
        zIndex: (theme) => theme.zIndex.snackbar,
        width: { xs: 'calc(100vw - 32px)', sm: 380 },
        maxWidth: 420,
        borderRadius: 3,
        overflow: 'hidden',
        border: 1,
        borderColor: 'grey.300',
      }}
    >
      <Box
        sx={{
          px: 2,
          py: 1.25,
          borderBottom: 1,
          borderColor: 'grey.200',
          backgroundColor: 'background.paper',
        }}
      >
        <Typography variant="textSM_semiBold" color="text.primary">
          {t('app.orgProfile.upload.tray.title', { count: pendingUploads.length })}
        </Typography>
      </Box>

      <Box
        sx={{
          maxHeight: 320,
          overflowY: 'auto',
          p: 1.5,
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
        }}
      >
        {pendingUploads.map((file) => (
          <FileUploadItem
            key={file.id}
            file={file}
            layout="small"
            onCancel={cancelUpload}
            onRetryUpload={retryUpload}
            onRemoveFailedUpload={removeFailed}
          />
        ))}
      </Box>
    </Paper>
  );
});
